import {
  Box,
  FormControlLabel,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AccordionSection from '../components/AccordionSection';
import ItemHeader from '../components/common/ItemHeader/ItemHeader';
import { DisableContext } from '../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../routes/Routes';
import { useGetBillingTierById } from '../services/react-query/billing_tiers';
import { mutationKeys } from '../services/react-query/mutationKeys';
import { useGetCurrentOrganization } from '../services/react-query/organization';
import { useCreateUserRole, useGetUserRoleById, useUpdateUserRole } from '../services/react-query/user_roles';
import { useGetCurrentUser } from '../services/react-query/users/useGetCurrentUser';
import { inputDividerStyling, listPageTableRowStyling } from '../styles/sxStyling';
import { UserSettingsCollapseKeys } from '../types/User';
import { Permission, UserRole, UserRoleModules } from '../types/UserRole';
import { Loading } from '../uikit';
import { errorPopulator } from '../utility/inputUtils';

enum PermissionActions {
  READ = 'read',
  WRITE = 'write',
}

type ModuleConfig = {
  label: string;
  value: UserRoleModules;
  supportedActions?: PermissionActions[];
};

const modules: ModuleConfig[] = [
  { label: 'Accounts', value: UserRoleModules.ACCOUNTS },
  { label: 'Events', value: UserRoleModules.EVENTS },
  { label: 'Tasks', value: UserRoleModules.TASKS },
  { label: 'Contacts', value: UserRoleModules.CONTACTS },
  { label: 'Employees', value: UserRoleModules.EMPLOYEES },
  { label: 'Preferences', value: UserRoleModules.PREFERENCES },
  { label: 'Task Templates', value: UserRoleModules.TASK_TEMPLATES },
  { label: 'Account Templates', value: UserRoleModules.ACCOUNT_TEMPLATES },
  { label: 'Contact Templates', value: UserRoleModules.CONTACT_TEMPLATES },
  { label: 'Employee Templates', value: UserRoleModules.EMPLOYEE_TEMPLATES },
  { label: 'Event Templates', value: UserRoleModules.EVENT_TEMPLATES },
  { label: 'Email Templates', value: UserRoleModules.EMAIL_TEMPLATES },
  { label: 'User Permissions', value: UserRoleModules.USER_ROLES },
  { label: 'Organization', value: UserRoleModules.ORGANIZATIONS },
  { label: 'Integrations', value: UserRoleModules.INTEGRATIONS },
  { label: 'Reports', value: UserRoleModules.REPORTS },
  { label: 'Form Submissions', value: UserRoleModules.FORM_SUBMISSIONS, supportedActions: [PermissionActions.READ] },
  { label: 'Form Templates', value: UserRoleModules.FORM_TEMPLATES },
];

const UserRoleItem: React.FC = () => {
  const navigate = useNavigate();
  const { data: currentUser } = useGetCurrentUser();
  const { uuid } = useParams();
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [saved, setSaved] = useState<boolean>(false);
  const { data: userRoleData, isLoading: isGetUserRoleByIdLoading } = useGetUserRoleById({ uuid: _.toString(uuid) });

  const [searchParams] = useSearchParams();
  const billingTierUuid = uuid === 'new' ? searchParams.get('tierUuid') : userRoleData?.billing_tier_uuid || null;
  const { data: billingTier } = useGetBillingTierById({ uuid: billingTierUuid });

  // Fetch organization data if the user is associated with an organization, not new and user role data is defined
  const enableOrganizationQuery = !_.isNil(currentUser?.organization_uuid) && uuid !== 'new' && userRoleData !== undefined;
  const { data: organizationData, isLoading: isGetOrganizationByIdLoading } = useGetCurrentOrganization({
    enabled: enableOrganizationQuery,
  });

  const saveCallback = () => {
    setSaved(true);
  };
  const { mutate: createUserRole } = useCreateUserRole({
    onSuccessCallback: (data: UserRole) => {
      navigate(`/${ROUTE_PATHS.USER_ROLES}/${data.uuid}`);
      saveCallback();
    },
  });
  const { mutate: updateUserRole } = useUpdateUserRole({
    uuid: _.toString(uuid),
    onSuccessCallback: saveCallback,
  });
  const isCreateUserRoleMutation = useIsMutating({ mutationKey: [mutationKeys.createUserRole] });
  const isUpdateUserRoleMutation = useIsMutating({ mutationKey: [mutationKeys.updateUserRole] });

  // Update user role data when the backend response is received
  useEffect(() => {
    if (userRoleData) {
      setUserRole({
        ...userRoleData,
        permissions: userRoleData.permissions ?? [], // Initialize permissions as an empty array if null
      });
    }
  }, [userRoleData]);

  const { disabled } = useContext(DisableContext);

  if (isGetOrganizationByIdLoading || isGetUserRoleByIdLoading || !billingTier) {
    return <Loading />;
  }

  // Check if the current role is the default role
  const isDefaultRole = organizationData?.default_role_uuid === uuid;

  const updateUserRoleData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newUserRole = {
      ...userRole,
      [event.target.id]: event.target.value,
    };
    setUserRole(newUserRole as UserRole);
  };

  const handleSave = () => {
    if (!userRole) return;

    const saveData = { ...userRole, billing_tier_uuid: billingTier.uuid };
    if (uuid === 'new') {
      createUserRole(saveData);
    } else {
      updateUserRole(saveData);
    }
  };

  // add a permission
  const addPermission = (permissions: Permission[], module: UserRoleModules, action: PermissionActions): Permission[] => {
    if (!permissions.some((perm) => perm.action === `${module}:${action}` && perm.resources === `*`)) {
      permissions.push({ action: `${module}:${action}`, resources: `*` });
    }
    return permissions;
  };

  // remove a permission
  const removePermission = (permissions: Permission[], module: UserRoleModules, action: PermissionActions): Permission[] => {
    return permissions.filter((perm) => !(perm.action === `${module}:${action}` && perm.resources === `*`));
  };

  // handle permission changes for a specific module and action
  const handlePermissionChange = (module: UserRoleModules, action: PermissionActions, checked: boolean) => {
    if (!userRole) return;

    // Initialize permissions if null
    const currentPermissions = userRole.permissions ?? [];

    // Create a new set of permissions to work with
    let newPermissions = [...currentPermissions];

    if (checked) {
      // If enabling write, also enable read
      if (action === PermissionActions.WRITE) {
        newPermissions = addPermission(newPermissions, module, PermissionActions.READ);
        newPermissions = addPermission(newPermissions, module, PermissionActions.WRITE);
      } else if (action === PermissionActions.READ) {
        newPermissions = addPermission(newPermissions, module, PermissionActions.READ);
      }
    } else {
      // If disabling read, also disable write
      if (action === PermissionActions.READ) {
        newPermissions = removePermission(newPermissions, module, PermissionActions.READ);
        newPermissions = removePermission(newPermissions, module, PermissionActions.WRITE);
      } else if (action === PermissionActions.WRITE) {
        newPermissions = removePermission(newPermissions, module, PermissionActions.WRITE);
      }
    }

    // Update the state with the new permissions using the previous state
    setUserRole(
      (prevState) =>
        ({
          ...prevState,
          permissions: _.uniqWith(newPermissions, _.isEqual),
        }) as UserRole
    );
  };

  // Check if the permission exists for the module and action
  const hasPermission = (module: UserRoleModules, action: PermissionActions) => {
    return userRole?.permissions.some((perm) => perm.action === `${module}:${action}` && perm.resources === `*`);
  };

  const tableItems = modules.map((module) => {
    const actions = module.supportedActions ?? Object.values(PermissionActions);

    return (
      <TableRow key={module.label} sx={{ ...listPageTableRowStyling }}>
        <TableCell>
          <Typography>{module.label}</Typography>
        </TableCell>
        <TableCell>
          <Box key={module.value} display="flex" alignItems="center" gap={4}>
            {actions.map((action) => (
              <FormControlLabel
                key={action}
                label={_.capitalize(action)}
                control={
                  <Switch
                    checked={hasPermission(module.value, action)}
                    onChange={(e) => handlePermissionChange(module.value, action, e.target.checked)}
                    disabled={disabled || _.includes(billingTier.restricted_actions, `${module.value}:${action}`)}
                  />
                }
              />
            ))}
          </Box>
        </TableCell>
      </TableRow>
    );
  });

  return (
    <Box>
      <ItemHeader
        title={`${uuid === 'new' ? 'Create' : 'Update'} User Permission`}
        onSave={handleSave}
        disableActions={Boolean(isCreateUserRoleMutation || isUpdateUserRoleMutation)}
      />
      <AccordionSection title="User Permission Information" collapseId={UserSettingsCollapseKeys.USER_ROLE_ITEM_INFORMATION}>
        <Box sx={inputDividerStyling}>
          <Box sx={inputDividerStyling}>
            <TextField
              id="name"
              label="Name"
              autoFocus
              onChange={updateUserRoleData}
              value={userRole?.name ?? ''}
              disabled={disabled || isDefaultRole}
              {...errorPopulator(userRole, 'name')}
            />
          </Box>
          <Box sx={inputDividerStyling}>
            <TextField
              id="description"
              label="Description"
              onChange={updateUserRoleData}
              value={userRole?.description ?? ''}
              disabled={disabled || isDefaultRole}
              {...errorPopulator(userRole, 'description')}
            />
          </Box>
          <Box width={'100%'} my={2}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Module</TableCell>
                    <TableCell>Access</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{tableItems}</TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </AccordionSection>
      <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
    </Box>
  );
};

export default UserRoleItem;
